import {Injector} from '@angular/core';
import {ContextService} from '../http/context.service';
import {NGSIResult} from '../../../shared/models/ngsi/ngsi-result';
import {Layer} from '../../../shared/models/layer.model';
import {UserService} from './user.service';
import {TcddTrainsLayerController} from '../../controllers/tcddTrains-layer.controller';
import {NGSIQuery} from '../../../shared/models/ngsi/ngsi-query.model';
import {NGSIEntity} from '../../../shared/utils/ngsi-result-util';
import {QueryOptions} from '../../../shared/models/ngsi/query-options';
import {catchError, forkJoin, interval, of, Subscription} from 'rxjs';
import {filter, map, switchMap} from 'rxjs/operators';
import {EntityRepresentation} from '../../../shared/models/visualization/representation.model';
import {IconVisualization} from '../../../shared/models/visualization/icon-visualization.model';
import {EntityResult} from '../../../shared/models/schema/entity-result';
import {TcddTrainDataManagerService} from '../tcdd-train-data-manager.service';
import {TcddTrainErrorDetectionService} from '../tcdd-train-error-detection.service';
import {NGSIFilter} from '../../../shared/models/ngsi/ngsi-filter.model';
import {SocketService} from '../http/socket.service';
import {NGSIEventType, Notification} from '../../../shared/models/notification.model';

export enum TCDDTRAIN_COLORS {
  GREEN = 'GREEN',
  RED = 'RED',
  RED_EAST = 'RED_EAST',
  RED_WEST = 'RED_WEST',
  RED_NORTH = 'RED_NORTH',
  RED_SOUTH = 'RED_SOUTH',
  RED_NORTHEAST = 'RED_NORTHEAST',
  RED_NORTHWEST = 'RED_NORTHWEST',
  RED_SOUTHEAST = 'RED_SOUTHEAST',
  RED_SOUTHWEST = 'RED_SOUTHWEST',
  GREEN_EAST = 'GREEN_EAST',
  GREEN_WEST = 'GREEN_WEST',
  GREEN_NORTH = 'GREEN_NORTH',
  GREEN_SOUTH = 'GREEN_SOUTH',
  GREEN_NORTHEAST = 'GREEN_NORTHEAST',
  GREEN_NORTHWEST = 'GREEN_NORTHWEST',
  GREEN_SOUTHEAST = 'GREEN_SOUTHEAST',
  GREEN_SOUTHWEST = 'GREEN_SOUTHWEST',
  GRAY = 'GRAY'
}

export enum TCDDTRAIN_DIRECTIONS {
  NORTH = 'N',
  SOUTH = 'S',
  EAST = 'E',
  WEST = 'W',
  NORTHEAST = 'NE',
  SOUTHEAST = 'SE',
  NORTHWEST = 'NW',
  SOUTHWEST = 'SW',
  NODIRECTION = '0'
}
export class TcddLayerService {

  shouldUnsubscribe: boolean = false;
  private cBrokerService: ContextService;
  private userService: UserService;
  public entities: Map<TCDDTRAIN_COLORS, NGSIResult[]>;
  private subscribers: TcddTrainsLayerController[];
  static instance: TcddLayerService;
  private tcddTrainDataManager: TcddTrainDataManagerService;
  private tcddTrainErrorDetection: TcddTrainErrorDetectionService;
  private refreshingIntervalId: Subscription;
  private backNgsiResultList: NGSIResult[] = [];
  protected socketService: SocketService;
  private isRefreshRedisWithId: boolean;
  private idListWithSubscription: number[] = [];

  private constructor(private injector: Injector) {
    this.cBrokerService = injector.get(ContextService);
    this.userService = injector.get(UserService);
    this.socketService = injector.get(SocketService);
    this.subscribers = [];
    this.entities = new Map();
    this.tcddTrainDataManager = new TcddTrainDataManagerService();
    this.tcddTrainErrorDetection = new TcddTrainErrorDetectionService();
    this.getAllTrainInfos();
    this.isRefreshRedisWithId = true;
    this.refreshingIntervalId = interval(60000).subscribe(() => {
      if (!this.shouldUnsubscribe) {
        this.getAllTrainInfosOfRedis();
      }
    });
    this.socketService.subscribe('3ee84a5b-0a3b-48e4-995f-b47c4dcc9090')
      .pipe(
        filter(() => !this.shouldUnsubscribe),
      )
      .subscribe(notification => {
        this.websocketCallback(notification);
      });

  }

  public addSubscriber(tcddTrainLayerController: TcddTrainsLayerController) {
    this.subscribers.push(tcddTrainLayerController);
  }

  public unsubscribe(tcddTrainLayerController : TcddTrainsLayerController){
    const entityIndex = this.subscribers.findIndex(e => {
      return e === tcddTrainLayerController;
    });
    if (entityIndex === null) {
      return;
    }
    this.subscribers.splice(entityIndex, 1);
    this.shouldUnsubscribe = true;
  }


  static getInstance(layer: Layer, injector: Injector) {
    if (TcddLayerService.instance == null) {
      TcddLayerService.instance = new TcddLayerService(injector);
    }
    return TcddLayerService.instance;
  }

  public getAllTrainInfosOfRedis() {
    forkJoin({
      position: this.cBrokerService.getEntity(this.getTcddCouplingPositionRedis(), this.redisQueryOptions())
        .pipe(catchError(() => of(null))),
      tcddTrain: this.cBrokerService.getEntity(this.getTcddTrainRedis(), this.redisQueryOptions())
        .pipe(catchError(() => of(null))),
      wagon: this.cBrokerService.getEntity(this.getWagonRedis(), this.redisQueryOptions())
        .pipe(catchError(() => of(null))),
      wagonStatus: this.cBrokerService.getEntity(this.getWagonStatusRedis(), this.redisQueryOptions())
        .pipe(catchError(() => of(null))),
      trainStatus: this.cBrokerService.getEntity(this.getTrainStatusRedis(), this.redisQueryOptions())
        .pipe(catchError(() => of(null))),
      deviceStatus: this.cBrokerService.getEntity(this.getDeviceStatusRedis(), this.redisQueryOptions())
        .pipe(catchError(() => of(null))),
    }).subscribe(result => {

      const mergedTrainData = result.position.getEntityResult().entity.map(position => {
        const allWagons = result.wagon.getEntityResult().entity;
        const activeWagons = allWagons.filter(
          wagon => wagon.trainRef?.object === position.activeTrainId.value
        );

        const passiveWagons = allWagons.filter(
          wagon => wagon.trainRef?.object === position.passiveTrainId?.value
        );

        const allTrainStatus = result.trainStatus.getEntityResult().entity;
        const activeTrainStatuses = allTrainStatus.filter(
          trainStatus => trainStatus.ref.object === position.activeTrainId.value
        );
        const passiveTrainStatuses = allTrainStatus.filter(
          trainStatus => trainStatus.ref.object === position.passiveTrainId?.value
        );

        const allDeviceStatus = result.deviceStatus.getEntityResult().entity;

        const activeDeviceStatuses = allDeviceStatus.filter(
          deviceStatus => deviceStatus.ref.object === position.activeTrainId.value
        );
        const passiveDeviceStatuses = allDeviceStatus.filter(
          deviceStatus => deviceStatus.ref.object === position.passiveTrainId.value
        );
        const activeWagonStatuses = activeWagons.map(wagon =>
          result.wagonStatus.getEntityResult().entity.find(
            wagonStatus => wagonStatus.ref.object === wagon.id
          )
        ).filter(Boolean);

        const passiveWagonStatuses = passiveWagons.map(wagon =>
          result.wagonStatus.getEntityResult().entity.find(
            wagonStatus => wagonStatus.ref.object === wagon.id
          )
        ).filter(Boolean);

        return {
          tcddCouplingPosition: position,
          wagon: {
            active: activeWagons,
            passive: passiveWagons
          },
          wagonStatus: {
            active: activeWagonStatuses,
            passive: passiveWagonStatuses
          },
          trainStatus: {
            active: activeTrainStatuses.length > 0 ? activeTrainStatuses[0] : '-',
            passive: passiveTrainStatuses.length > 0 ? passiveTrainStatuses[0] : '-'
          },
          deviceStatus: {
            active: activeDeviceStatuses.length > 0 ? activeDeviceStatuses[0] : '-',
            passive: passiveDeviceStatuses.length > 0 ? passiveDeviceStatuses[0] : '-'
          },
        };
      });
      const passiveIds = [];
      const activeIds = [];
      let finalResult: NGSIResult[] = [];
      mergedTrainData.sort((a, b) => {
        const dateA = new Date(a.tcddCouplingPosition.direction.observedAt).getTime();
        const dateB = new Date(b.tcddCouplingPosition.direction.observedAt).getTime();
        return dateB - dateA;
      });

      mergedTrainData.forEach(item => {
        const passiveId = item.tcddCouplingPosition.passiveTrainId.value;
        const activeId = item.tcddCouplingPosition.activeTrainId.value;

        if (passiveId && passiveId !== '-') {
          passiveIds.push(passiveId);
        }
        activeIds.push(activeId);
      });

      mergedTrainData.forEach(item => {
        const activeId = item.tcddCouplingPosition.activeTrainId.value;

        if (!passiveIds.includes(activeId)) {
          finalResult.push(item);
        }
      });

      if (this.isRefreshRedisWithId) {
        this.isRefreshRedisWithId = false;
        const allTcddTrain = result.tcddTrain.getEntityResult().entity;
        const allPosition = result.position.getEntityResult().entity;
        const tcddTrainIds = allTcddTrain.map(train => train.id.replace('TcddTrain:', 'TcddCouplingPosition:'));
        const allPositionIds = new Set(allPosition.map(pos => pos.id));
        const filteredTcddPositionIds = tcddTrainIds.filter(id => !allPositionIds.has(id) && !passiveIds.includes(id.replace('TcddCouplingPosition:', 'TcddTrain:')));
        const observables = filteredTcddPositionIds.map(id => this.cBrokerService.getEntity(this.getTcddCouplingPositionRedisWithId(id), this.redisQueryOptions())
          .pipe(catchError(() => of(null))));

        forkJoin(observables).subscribe({
          next: (results) => {
          },
          error: (err) => {
          }
        });
      }
      this.checkColorForTcddTrains(finalResult);
    });
  }

  protected redisQueryOptions() {
    return new QueryOptions(false, false, false, true);
  }




  public getAllTrainInfos() {
    this.cBrokerService
      .getEntity(this.getCouplingPosition(), new QueryOptions(false))
      .pipe(catchError(() => of(null)))
      .subscribe(response => {
        const entityResult = (response as NGSIResult)?.getEntityResult();
        if (entityResult?.entity) {
          const trainPairs: { active: string; passive?: string }[] = entityResult.entity
            .filter(e => e.isActive)
            .map(e => ({
              active: e.activeTrainId.value,
              passive: e.isCoupling.value === 1 ? e.passiveTrainId.value : undefined
            }));

          if (trainPairs.length === 0) return;

          const wagonRequests = this.getCreateWagonRequest(trainPairs);

          forkJoin(wagonRequests).subscribe(allWagonData => {
            if (allWagonData.length === 0) return;

            const wagonStatusRequests = allWagonData.map(wagonData =>
              forkJoin({
                active: wagonData.active.length
                  ? forkJoin(
                    wagonData.active.map(item =>
                      this.cBrokerService.getEntity(
                        this.getWagonStatus(item.id),
                        new QueryOptions(false, false, false, true)
                      ).pipe(
                        catchError(() => of(null)),
                        map(response => (response as NGSIResult)?.getEntityResult()?.entity || '-')
                      )
                    )
                  )
                  : of([]),

                passive: wagonData.passive.length
                  ? forkJoin(
                    wagonData.passive.map(item =>
                      this.cBrokerService.getEntity(
                        this.getWagonStatus(item.id),
                        new QueryOptions(false, false, false, true)
                      ).pipe(
                        catchError(() => of(null)),
                        map(response => (response as NGSIResult)?.getEntityResult()?.entity || '-')
                      )
                    )
                  )
                  : of([])
              })
            );

            forkJoin(wagonStatusRequests).pipe(
              switchMap(wagonStatuses => {
                const trainStatusRequests = this.getCreateTrainStatusRequest(trainPairs);
                const deviceStatusRequests = this.getCreateDeviceStatusRequest(trainPairs);

                return forkJoin([
                  forkJoin(trainStatusRequests),
                  forkJoin(deviceStatusRequests)
                ]).pipe(
                  map(([trainStatuses, deviceStatuses]) => ({
                    wagonStatuses,
                    trainStatuses,
                    deviceStatuses
                  }))
                );
              })
            ).subscribe({
              next: ({wagonStatuses, trainStatuses, deviceStatuses}) => {
                let result = entityResult.entity.map((tcddCouplingPosition, index) => ({
                  tcddCouplingPosition,
                  wagon: {
                    active: allWagonData[index]?.active || [],
                    passive: allWagonData[index]?.passive || []
                  },
                  wagonStatus: {
                    active: wagonStatuses[index]?.active || [],
                    passive: wagonStatuses[index]?.passive || []
                  },
                  trainStatus: {
                    active: trainStatuses[index]?.active || '-',
                    passive: trainStatuses[index]?.passive || '-'
                  },
                  deviceStatus: {
                    active: deviceStatuses[index]?.active || '-',
                    passive: deviceStatuses[index]?.passive || '-'
                  }
                }));

                const passiveIds = [];
                const finalResult: typeof result = [];
                result.sort((a, b) => {
                  const dateA = new Date(a.tcddCouplingPosition.direction.observedAt).getTime();
                  const dateB = new Date(b.tcddCouplingPosition.direction.observedAt).getTime();
                  return dateB - dateA;
                });

                result.forEach(item => {
                  const passiveId = item.tcddCouplingPosition.passiveTrainId.value;
                  const activeId = item.tcddCouplingPosition.activeTrainId.value;

                  if (passiveId && passiveId !== '-') {
                    passiveIds.push(passiveId);
                  }

                  if (!passiveIds.includes(activeId)) {
                    finalResult.push(item);
                  }
                });
                this.checkColorForTcddTrains(finalResult);
              }
            });
          });
        }
      });
  }


  public notifySubscriber() {
    try {
      this.subscribers.map(controller => {
        controller.getTcddCouplingPositions();
      });
    } catch (error) {
      console.error('Error occurred while notifying layers:', error);
    }
  }

  public getTcddCouplingPositionForRepresentation(entityRep: EntityRepresentation) {
    const icon = (entityRep.visualization as IconVisualization).icon;
    const color = icon.split('-').pop()?.split('.')[0].toUpperCase();
    const colorValue = this.convertStringToEnum(color);
    if (!this.entities.has(colorValue)) {
      return [] as NGSIResult[];
    }
    return this.entities.get(colorValue);
  }

  convertStringToEnum(color: String): TCDDTRAIN_COLORS {
    const enumKeys = Object.keys(TCDDTRAIN_COLORS).filter(k => typeof TCDDTRAIN_COLORS[k as keyof typeof TCDDTRAIN_COLORS] === 'string') as (keyof typeof TCDDTRAIN_COLORS)[];

    for (const key of enumKeys) {
      if (TCDDTRAIN_COLORS[key] === color) {
        return TCDDTRAIN_COLORS[key];
      }
    }
    return TCDDTRAIN_COLORS.GREEN;
  }


  public checkColorForTcddTrains(trainsInfo) {
    const tempEntities: Map<TCDDTRAIN_COLORS, NGSIResult[]> = new Map();

    trainsInfo.forEach(info => {

      const entityData = {
        id: info.tcddCouplingPosition.id,
        type: info.tcddCouplingPosition.type,
        direction: info.tcddCouplingPosition.direction,
        activeHmiVehicleNo: [{
          type: 'Property',
          value: info.tcddCouplingPosition.activeHmiVehicleNo ? '' + info.tcddCouplingPosition.activeHmiVehicleNo.value : '-'
        }],
        passiveHmiVehicleNo: [{
          type: 'Property',
          value: info.tcddCouplingPosition.passiveHmiVehicleNo ? '' + info.tcddCouplingPosition.passiveHmiVehicleNo.value : '-'
        }],
        distanceTraveled: [{
          type: 'Property',
          value: info.tcddCouplingPosition.distanceTraveled ? info.tcddCouplingPosition.distanceTraveled.value : 0
        }],
        activeTrainId: [{type: 'Property', value: info.tcddCouplingPosition.activeTrainId.value}],
        isActive: [{type: 'Property', value: info.tcddCouplingPosition.isActive.value}],
        isCoupling: [{type: 'Property', value: info.tcddCouplingPosition.isCoupling.value}],
        location: [{
          type: 'GeoProperty',
          value: info.tcddCouplingPosition.location.value,
          observedAt: info.tcddCouplingPosition.location.observedAt
        }],
        passiveTrainId: [{type: 'Property', value: info.tcddCouplingPosition.passiveTrainId.value}],
        speed: [{
          type: 'Property',
          value: info.tcddCouplingPosition.speed ? info.tcddCouplingPosition.speed.value : null
        }]
      };

      const queryOptions = {
        isTemporal: true,
        keyValues: false,
        sysAttrs: false,
        redis: false
      };


      const hasConnectionProblem = this.controlTrainConnection(info.deviceStatus.active.lifeSign ? info.deviceStatus.active.lifeSign.observedAt : null);

      const hasError = this.controlTrainColor(entityData, info.trainStatus, info.wagon, info.wagonStatus);

      const entityResult = new EntityResult(entityData, queryOptions);

      let ngsiResult = new NGSIResult(entityResult);

      const index = this.backNgsiResultList.findIndex(e =>
        e.getEntityResult().entity.id === entityResult.entity.id
      );
      if (index === -1) {
        this.backNgsiResultList.push(ngsiResult);
      } else {
        const reversedCoordinates = [
          this.backNgsiResultList[index].getEntityResult().entity.location[0].value.coordinates[1],
          this.backNgsiResultList[index].getEntityResult().entity.location[0].value.coordinates[0],
        ];
        const coordinates = [
          this.backNgsiResultList[index].getEntityResult().entity.location[0].value.coordinates[0],
          this.backNgsiResultList[index].getEntityResult().entity.location[0].value.coordinates[1],
        ];
        ngsiResult = this.backNgsiResultList[index];
        if ( this.idListWithSubscription.includes(entityResult.entity.id)) {
          ngsiResult.getEntityResult().entity.location[0].value.coordinates = reversedCoordinates;
        } else {
          ngsiResult.getEntityResult().entity.location[0].value.coordinates = coordinates;
        }
      }
      if (hasConnectionProblem) {
        if (!tempEntities.has(TCDDTRAIN_COLORS.GRAY)) {
          tempEntities.set(TCDDTRAIN_COLORS.GRAY, [ngsiResult]);
        } else {
          tempEntities.get(TCDDTRAIN_COLORS.GRAY).push(ngsiResult);
        }
      } else if (hasError || this.hasDeviceError(info.deviceStatus)) {
        if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.NODIRECTION) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.EAST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_EAST)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_EAST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_EAST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.WEST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_WEST)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_WEST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_WEST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.NORTHEAST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_NORTHEAST)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_NORTHEAST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_NORTHEAST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.NORTHWEST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_NORTHWEST)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_NORTHWEST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_NORTHWEST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.SOUTHEAST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_SOUTHEAST)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_SOUTHEAST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_SOUTHEAST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.SOUTHWEST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_SOUTHWEST)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_SOUTHWEST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_SOUTHWEST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.SOUTH) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_SOUTH)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_SOUTH, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_SOUTH).push(ngsiResult);
          }
        } else {
          if (!tempEntities.has(TCDDTRAIN_COLORS.RED_NORTH)) {
            tempEntities.set(TCDDTRAIN_COLORS.RED_NORTH, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.RED_NORTH).push(ngsiResult);
          }
        }
      } else {
        if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.NODIRECTION) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.EAST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_EAST)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_EAST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_EAST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.WEST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_WEST)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_WEST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_WEST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.NORTHEAST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_NORTHEAST)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_NORTHEAST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_NORTHEAST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.NORTHWEST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_NORTHWEST)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_NORTHWEST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_NORTHWEST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.SOUTHEAST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_SOUTHEAST)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_SOUTHEAST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_SOUTHEAST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.SOUTHWEST) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_SOUTHWEST)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_SOUTHWEST, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_SOUTHWEST).push(ngsiResult);
          }
        } else if (entityData.direction.value === TCDDTRAIN_DIRECTIONS.SOUTH) {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_SOUTH)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_SOUTH, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_SOUTH).push(ngsiResult);
          }
        } else {
          if (!tempEntities.has(TCDDTRAIN_COLORS.GREEN_NORTH)) {
            tempEntities.set(TCDDTRAIN_COLORS.GREEN_NORTH, [ngsiResult]);
          } else {
            tempEntities.get(TCDDTRAIN_COLORS.GREEN_NORTH).push(ngsiResult);
          }
        }
      }
    });
    this.entities = tempEntities;
    this.idListWithSubscription = [];
    this.notifySubscriber();
  }

  protected hasDeviceError(deviceStatus) {
    if (deviceStatus?.active[0]?.deviceErrors?.value?.includes('1')) {
      return true;
    } else {
      return false;
    }
  }

  protected controlTrainColor(position, trainStatus, wagons, wagonStatuses) {
    if (position.isCoupling[0].value !== 1) {
      const trainData = this.tcddTrainDataManager.processTrainData(trainStatus.active.length ? trainStatus.active[0] : trainStatus.active);
      const wagonData = this.tcddTrainDataManager.processWagonsData(wagons.active, wagonStatuses.active);
      const hasError = this.tcddTrainErrorDetection.hasSystemErrors(trainData, wagonData);
      return hasError;
    } else {
      const trainData = this.tcddTrainDataManager.processTrainData(trainStatus.active.length ? trainStatus.active[0] : trainStatus.active);
      const wagonData = this.tcddTrainDataManager.processWagonsData(wagons.active, wagonStatuses.active);
      const hasError = this.tcddTrainErrorDetection.hasSystemErrors(trainData, wagonData);
      const trainDataPassive = this.tcddTrainDataManager.processTrainData(trainStatus.active.length ? trainStatus.active[0] : trainStatus.active);
      const wagonDataPassive = this.tcddTrainDataManager.processWagonsData(wagons.active, wagonStatuses.active);
      const hasErrorPassive = this.tcddTrainErrorDetection.hasSystemErrors(trainDataPassive, wagonDataPassive);
      return hasError || hasErrorPassive;
    }
  }

  protected controlTrainConnection(observedAt: string) {
    if (observedAt !== null) {
      const observedTime = new Date(observedAt);
      const currentTime = new Date();
      const timeDifference = currentTime.getTime() - observedTime.getTime();

      if (timeDifference >= 10 * 60 * 1000) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  }


  protected getCouplingPosition() {
    return new NGSIQuery({
      filter: {
        type: [NGSIEntity.TCDDCouplingPosition],
      },
      lastNDays: 365,
      sortFields: 'id',
      sortDirections: 'DESC',
      distinctOn: 'id'
    });
  }

  protected getAllWagon(trainId) {
    return new NGSIQuery({
      filter: {
        type: [NGSIEntity.WAGON],
        q: {
          jsonClass: 'NGSIQueryTerm',
          path: {
            jsonClass: 'NGSIPath',
            propOrRelPath: [
              'trainRef'
            ],
            isTemporal: false,
            isPropOrRelArray: false
          },
          op: '==',
          value: [
            trainId
          ]
        }
      },
      lastNDays: 365
    });
  }

  protected getTcddCouplingPositionRedis() {
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.type = ['TcddCouplingPosition'];

    return query;
  }

  protected getTcddCouplingPositionRedisWithId(positionId) {
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.id = [positionId];

    return query;
  }

  protected getWagonRedis() {
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.type = ['Wagon'];

    return query;
  }
  protected getWagonStatusRedis() {
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.type = ['WagonStatus'];

    return query;
  }

  protected getTcddTrainRedis() {
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.type = ['TcddTrain'];

    return query;
  }

  protected getTrainStatusRedis() {
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.type = ['TrainStatus'];

    return query;
  }
  protected getDeviceStatusRedis() {
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.type = ['DeviceStatus'];

    return query;
  }


  protected getWagonStatus(wagonId) {
    const deviceStatusId = wagonId.replace('Wagon', 'WagonStatus');
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.id = [deviceStatusId];

    return query;
  }

  protected getTrainStatus(trainId) {
    const deviceStatusId = trainId.replace('TcddTrain', 'TrainStatus');
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.id = [deviceStatusId];

    return query;
  }


  protected getDeviceStatus(trainId) {
    const deviceStatusId = trainId.replace('TcddTrain', 'DeviceStatus');
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.id = [deviceStatusId];

    return query;
  }

  private getCreateWagonRequest(trainPairs: { active: string; passive?: string }[]) {
    return trainPairs.map(pair =>
      forkJoin({
        active: this.cBrokerService.getEntity(this.getAllWagon(pair.active), new QueryOptions(false)).pipe(
          catchError(() => of(null)),
          map(responseWagon => (responseWagon as NGSIResult)?.getEntityResult()?.entity?.map(e => e) || [])
        ),
        passive: pair.passive
          ? this.cBrokerService.getEntity(this.getAllWagon(pair.passive), new QueryOptions(false)).pipe(
            catchError(() => of(null)),
            map(responseWagon => (responseWagon as NGSIResult)?.getEntityResult()?.entity?.map(e => e) || [])
          )
          : of([])
      })
    );
  }

  private getCreateTrainStatusRequest(trainPairs: { active: string; passive?: string }[]) {
    return trainPairs.map(pair =>
      forkJoin({
        active: this.cBrokerService.getEntity(this.getTrainStatus(pair.active), new QueryOptions(false, false, false, true)).pipe(
          catchError(() => of(null)),
          map(responseTrainStatus => (responseTrainStatus as NGSIResult)?.getEntityResult()?.entity || '-')
        ),
        passive: pair.passive
          ? this.cBrokerService.getEntity(this.getTrainStatus(pair.passive), new QueryOptions(false, false, false, true)).pipe(
            catchError(() => of(null)),
            map(responseTrainStatus => (responseTrainStatus as NGSIResult)?.getEntityResult()?.entity || '-')
          )
          : of('-')
      })
    );
  }

  private getCreateDeviceStatusRequest(trainPairs: { active: string; passive?: string }[]) {
    return trainPairs.map(pair =>
      forkJoin({
        active: this.cBrokerService.getEntity(this.getDeviceStatus(pair.active), new QueryOptions(false, false, false, true)).pipe(
          catchError(() => of(null)),
          map(responseDeviceStatus => (responseDeviceStatus as NGSIResult)?.getEntityResult()?.entity || '-')
        ),
        passive: pair.passive
          ? this.cBrokerService.getEntity(this.getDeviceStatus(pair.passive), new QueryOptions(false, false, false, true)).pipe(
            catchError(() => of(null)),
            map(responseDeviceStatus => (responseDeviceStatus as NGSIResult)?.getEntityResult()?.entity || '-')
          )
          : of('-')
      })
    );
  }

  protected websocketCallback(notification: Notification): void {
    if (notification.eventClass === 'NGSIEvent') {
      if (notification.entityId.includes('TcddCouplingPosition') && notification.eventPayload.eventType === NGSIEventType.CREATE) {
        const index = this.backNgsiResultList.findIndex(e =>
          e.getEntityResult().entity.id === notification.eventPayload.content.id
        );
        if (index !== -1) {
          const coordinates = [
            notification.eventPayload.content.location.value.coordinates[0],
            notification.eventPayload.content.location.value.coordinates[1],
          ];

          this.backNgsiResultList[index].getEntityResult().entity.location[0].value.coordinates =
            coordinates;
          if (!this.idListWithSubscription.includes( notification.eventPayload.content.id)) {
            this.idListWithSubscription.push( notification.eventPayload.content.id);
          }
        }
      }
    }
  }
}
